/**
 * Utilities for normalizing errors before sending to Rollbar.
 *
 * Rollbar expects an Error object as the first argument for proper stack trace
 * and error details extraction. Custom metadata should be serializable (no Error objects).
 */

/**
 * Serializable error details for Rollbar custom metadata.
 * These fields will appear in Rollbar UI under "custom.*"
 */
export interface ISerializedErrorDetails {
  /** Original error name (e.g., 'WallabyError', 'TypeError') */
  name: string;
  /** Error message */
  message: string;
  /** Error code if available (e.g., 'NETWORK_ERROR') */
  errorCode?: string;
  /** HTTP status if available */
  status?: number;
  /** Wallaby request ID for backend correlation */
  requestId?: string;
  /** Additional error data (serialized) */
  data?: Record<string, unknown>;
}

/**
 * React Query context for Rollbar custom metadata.
 * Provides query/mutation identification without non-serializable data.
 */
export interface IReactQueryContext {
  /** Query key array (e.g., ['wallaby', 'walletData', 'balances', 'user-id']) */
  queryKey?: readonly unknown[];
  /** Mutation key if available */
  mutationKey?: readonly unknown[];
  /** Serialized error details */
  error: ISerializedErrorDetails;
  /** Index signature for Record<string, unknown> compatibility */
  [key: string]: unknown;
}

/**
 * Checks if value is an Error-like object (has message property).
 */
function isErrorLike(value: unknown): value is Error {
  return (
    value !== null &&
    typeof value === 'object' &&
    'message' in value &&
    typeof (value as Error).message === 'string'
  );
}

/**
 * Extracts error code from various error types.
 * Handles WallabyError and other custom error types with errorCode property.
 */
function extractErrorCode(error: unknown): string | undefined {
  if (
    error !== null &&
    typeof error === 'object' &&
    'errorCode' in error &&
    typeof (error as { errorCode: unknown }).errorCode === 'string'
  ) {
    return (error as { errorCode: string }).errorCode;
  }
  return undefined;
}

/**
 * Extracts HTTP status from error if available. Falls back to the response
 * body's `data.statusCode`: some Wallaby SDK paths re-wrap errors without
 * `status` but keep the response data (RWA-420).
 */
export function extractStatus(error: unknown): number | undefined {
  if (error === null || typeof error !== 'object') return undefined;
  const { status, data } = error as { status?: unknown; data?: unknown };
  if (typeof status === 'number') return status;
  const dataStatus =
    data !== null && typeof data === 'object'
      ? (data as { statusCode?: unknown }).statusCode
      : undefined;
  return typeof dataStatus === 'number' ? dataStatus : undefined;
}

/**
 * Extracts requestId from error for Wallaby backend correlation.
 * Checks both top-level requestId and nested in data.requestId.
 */
function extractRequestId(error: unknown): string | undefined {
  if (error === null || typeof error !== 'object') {
    return undefined;
  }

  // Check top-level requestId
  if (
    'requestId' in error &&
    typeof (error as { requestId: unknown }).requestId === 'string'
  ) {
    return (error as { requestId: string }).requestId;
  }

  // Check nested in data.requestId (common in API responses)
  if (
    'data' in error &&
    typeof (error as { data: unknown }).data === 'object'
  ) {
    const data = (error as { data: Record<string, unknown> }).data;
    if (
      data !== null &&
      'requestId' in data &&
      typeof data.requestId === 'string'
    ) {
      return data.requestId;
    }
  }

  return undefined;
}

/**
 * Extracts the backend-supplied `details` string from `error.data.details`,
 * if present. This is the field that carries the actual cause of a failure
 * (e.g. "Failed to sign transaction") when the top-level error message is
 * generic.
 */
export function extractErrorDetails(error: unknown): string | undefined {
  if (
    error !== null &&
    typeof error === 'object' &&
    'data' in error &&
    typeof (error as { data: unknown }).data === 'object'
  ) {
    const data = (error as { data: Record<string, unknown> | null }).data;
    if (
      data !== null &&
      'details' in data &&
      typeof data.details === 'string'
    ) {
      return data.details;
    }
  }
  return undefined;
}

/**
 * Extracts additional data from error, excluding non-serializable fields.
 */
function extractErrorData(error: unknown): Record<string, unknown> | undefined {
  if (
    error !== null &&
    typeof error === 'object' &&
    'data' in error &&
    typeof (error as { data: unknown }).data === 'object'
  ) {
    const data = (error as { data: Record<string, unknown> }).data;
    if (data === null) return undefined;

    // Filter out Error objects from data to avoid {} serialization
    const serializable: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (value instanceof Error) {
        // Serialize nested Error objects
        serializable[key] = {
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
      } else if (typeof value !== 'function') {
        serializable[key] = value;
      }
    }
    return Object.keys(serializable).length > 0 ? serializable : undefined;
  }
  return undefined;
}

/**
 * Normalizes any error value into a proper Error object.
 * Preserves original stack trace when possible.
 *
 * @param error - The error to normalize (can be Error, string, object, etc.)
 * @returns A proper Error object with name, message, and stack
 *
 * @example
 * ```ts
 * // Error object - preserved
 * normalizeError(new TypeError('failed')) // TypeError with original stack
 *
 * // String - converted to Error
 * normalizeError('something failed') // Error('something failed')
 *
 * // Object with message - converted to Error
 * normalizeError({ message: 'failed', code: 'ERR' }) // Error('failed')
 *
 * // Unknown - generic Error
 * normalizeError(null) // Error('Unknown error')
 * ```
 */
export function normalizeError(error: unknown): Error {
  // Already an Error - return as-is to preserve stack trace
  if (error instanceof Error) {
    return error;
  }

  // String - convert to Error
  if (typeof error === 'string') {
    return new Error(error);
  }

  // Error-like object (has message property)
  if (isErrorLike(error)) {
    const normalizedError = new Error(error.message);
    // Preserve error name if available
    if ('name' in error && typeof error.name === 'string') {
      normalizedError.name = error.name;
    }
    return normalizedError;
  }

  // Object - try to stringify
  if (error !== null && typeof error === 'object') {
    try {
      return new Error(JSON.stringify(error));
    } catch {
      return new Error('Unknown error (non-serializable object)');
    }
  }

  // Fallback for null, undefined, numbers, etc.
  return new Error(error === null ? 'null' : String(error));
}

/**
 * Serializes error details for Rollbar custom metadata.
 * Extracts all relevant information without including Error objects.
 *
 * @param error - The original error
 * @returns Serializable error details
 */
export function serializeErrorDetails(error: unknown): ISerializedErrorDetails {
  const normalizedError = normalizeError(error);

  return {
    name: normalizedError.name,
    message: normalizedError.message,
    errorCode: extractErrorCode(error),
    status: extractStatus(error),
    requestId: extractRequestId(error),
    data: extractErrorData(error),
  };
}

/**
 * Creates React Query context for Rollbar reporting.
 * Ensures all data is serializable (no Error objects in metadata).
 *
 * @param options - Query/mutation context options
 * @returns Serializable context for Rollbar custom metadata
 *
 * @example
 * ```ts
 * // Query error context
 * const context = createReactQueryContext({
 *   queryKey: ['wallaby', 'balances', userId],
 *   error: wallabyError,
 * });
 * // Result: { queryKey: [...], error: { name: 'WallabyError', message: '...', errorCode: 'NETWORK_ERROR' } }
 *
 * // Mutation error context
 * const context = createReactQueryContext({
 *   mutationKey: ['transfer', 'submit'],
 *   error: transferError,
 * });
 * ```
 */
export function createReactQueryContext(options: {
  queryKey?: readonly unknown[];
  mutationKey?: readonly unknown[];
  error: unknown;
}): IReactQueryContext {
  const context: IReactQueryContext = {
    error: serializeErrorDetails(options.error),
  };

  if (options.queryKey) {
    context.queryKey = options.queryKey;
  }

  if (options.mutationKey) {
    context.mutationKey = options.mutationKey;
  }

  return context;
}
